import _ from "lodash";
import React from "react";
import { Button, IconButton, TextInput } from "@toddle-edu/ds-web";
import { useI18n } from "Hooks";
import classNames from "classnames";
import classes from "./PollQuestionAndOptions.scss";
import {
  AddCircleOutlined,
  DeleteOutlined,
  DotsSixVerticalOutlined,
  SmileyHappyOutlined,
  WarningCircleOutlined,
} from "@toddle-edu/ds-icons";

import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  KeyboardSensor,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import update from "immutability-helper";
import EmojiPicker from "UIComponents/EmojiPicker";
import { generateRandomId } from "Utils/GenerateRandomId";

export const initialEmojiPickerState = {
  ref: null,
  index: null,
};

const EmojiIcon = ({ icon }) => {
  return <div>{icon.native}</div>;
};

const emojiPickerPositions = ["right", "bottom", "left", "top"];

const SortableItem = ({ option, index, children }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: option.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className={classes.pollOptionContainer}>
      {children}
      <IconButton
        {...attributes}
        {...listeners}
        dsVersion="2.0"
        variant="neutral"
        type="plain"
        size="small"
        icon={<DotsSixVerticalOutlined />}
        className={classNames(classes.dragHandle, classes.pollOptionIcon)}
      />
    </div>
  );
};

const PollsQuestionAndOptions = ({ poll, setPoll }) => {
  const { t } = useI18n(["common"]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor)
  );

  const [emojiPickerContainer, setEmojiPickerContainer] = React.useState(
    initialEmojiPickerState
  );

  const onQuestionChange = e => {
    const question = e?.target?.value;
    setPoll({
      ...poll,
      question,
    });
  };

  const onAddOption = () => {
    if (_.size(poll.options) >= 5) return;
    const newOption = {
      label: "",
      icon: null,
      id: generateRandomId(),
    };
    setPoll({
      ...poll,
      options: [...poll.options, newOption],
    });
  };

  const onDeleteOption = index => {
    const newOptions = _.filter(poll.options, (option, i) => {
      return i !== index;
    });
    setPoll({
      ...poll,
      options: newOptions,
    });
  };

  const updateOption = (index, changes) => {
    const updatedOptions = update(poll.options, {
      [index]: { $merge: changes },
    });
    setPoll({
      ...poll,
      options: updatedOptions,
    });
  };

  const resetEmojiPickerState = () => {
    setEmojiPickerContainer(initialEmojiPickerState);
  };

  const onChangeOptionValue = (index, e) => {
    const label = e?.target?.value;
    updateOption(index, { label });
  };

  const onClickOptionIcon = (index, e) => {
    const element = e?.target;
    setEmojiPickerContainer({
      ref: element,
      index,
    });
  };

  const onSelectEmoji = emoji => {
    const { index } = emojiPickerContainer;
    updateOption(index, { icon: emoji });
    resetEmojiPickerState();
  };

  const onDragEnd = event => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const oldIndex = poll.options.findIndex(opt => opt.id === active.id);
      const newIndex = poll.options.findIndex(opt => opt.id === over?.id);
      if (oldIndex !== -1 && newIndex !== -1) {
        const reordered = arrayMove(poll.options, oldIndex, newIndex);
        setPoll({ ...poll, options: reordered });
      }
    }
  };

  return (
    <div className={classes.pollsAndDetailsContainer}>
      <div className={classes.pollQuestionInput}>
        <TextInput
          size="medium"
          label={t("common:your_question")}
          placeholder={t("common:type_here")}
          value={poll.question}
          onChange={onQuestionChange}
          dsVersion="2.0"
        />
      </div>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={onDragEnd}
      >
        <SortableContext
          items={_.map(poll.options, opt => opt.id)}
          strategy={verticalListSortingStrategy}
        >
          {_.map(poll.options, (option, index) => {
            return (
              <SortableItem key={option.id} option={option} index={index}>
                <div className={classes.pollOptionInput}>
                  <TextInput
                    size="medium"
                    label={t("common:option_label", {
                      label: index + 1,
                    })}
                    placeholder={t("common:type_here")}
                    value={option.label}
                    leadingIcon={
                      option.icon ? (
                        <EmojiIcon icon={option.icon} />
                      ) : (
                        <SmileyHappyOutlined />
                      )
                    }
                    onChange={onChangeOptionValue.bind(null, index)}
                    onLeadingIconClick={onClickOptionIcon.bind(null, index)}
                    dsVersion="2.0"
                  />
                </div>
                <IconButton
                  dsVersion="2.0"
                  variant="neutral"
                  type="plain"
                  size="small"
                  icon={<DeleteOutlined />}
                  className={classes.pollOptionIcon}
                  disabled={_.size(poll.options) <= 2}
                  onClick={() => onDeleteOption(index)}
                />
              </SortableItem>
            );
          })}
        </SortableContext>
      </DndContext>
      <div className={classes.addOptionContainer}>
        {_.lt(_.size(poll.options), 5) ? (
          <Button
            dsVersion="2.0"
            variant="progressive"
            type="inline"
            className={classes.addOptionButton}
            icon={<AddCircleOutlined />}
            onClick={onAddOption}
          >
            {t("common:add_option")}
          </Button>
        ) : (
          <div className={classes.addOptionContainer}>
            <WarningCircleOutlined variant="critical" />
            <div className="text-body">
              {t("common:option_max_create_limit")}
            </div>
          </div>
        )}
      </div>

      {emojiPickerContainer?.ref ? (
        <EmojiPicker
          isOpen={true}
          onEmojiSelect={onSelectEmoji}
          popoverProps={{
            positions: emojiPickerPositions,
            padding: 0,
            reposition: true,
            isChildrenRefPassed: true,
            childrenRef: { current: emojiPickerContainer.ref },
          }}
          onClickOutside={resetEmojiPickerState}
        />
      ) : null}
    </div>
  );
};

export default PollsQuestionAndOptions;
